import { useState, ReactNode, MouseEvent } from "react";
import { useTheme, Theme } from "@mui/material/styles";
import { Grid, Typography, Popover, Box } from "@mui/material";
import { IconArrowUpRight, IconArrowDownRight } from "@tabler/icons-react";
import { lightGreen, red } from "@mui/material/colors";

export interface StatItem {
  /** Unique key for this stat */
  key: string;
  /** Icon to display */
  icon: ReactNode;
  /** Primary value to display */
  value: string | number;
  /** Optional Title */
  title?: string;
  /** Label/title for the stat */
  label: string;
  /** Color for the stat (theme palette key or hex) */
  color?: string;
  /** Optional delta value for comparison */
  delta?: number;
}

export interface StatsBarProps {
  /** Array of stats to display */
  stats: StatItem[];
  /** Typography variant for values */
  variant?: "h3" | "h4" | "h5" | "body1";
}

/** Returns the appropriate color from theme or as hex */
const getColor = (theme: Theme, color?: string): string => {
  if (!color) return theme.palette.primary.main;

  // Check if it's a theme palette key
  const paletteKeys = [
    "primary",
    "secondary",
    "error",
    "warning",
    "info",
    "success",
  ] as const;
  for (const key of paletteKeys) {
    if (color === key) {
      return theme.palette[key].main;
    }
  }

  // Otherwise treat as hex/rgb color
  return color;
};

/** Returns the appropriate delta icon */
const getDeltaIcon = (delta?: number) => {
  if (!delta) return null;

  if (delta > 0) {
    return (
      <IconArrowUpRight
        size={20}
        style={{ marginLeft: 6, color: lightGreen[500] }}
      />
    );
  } else if (delta < 0) {
    return (
      <IconArrowDownRight
        size={20}
        style={{ marginLeft: 6, color: red[300] }}
      />
    );
  }

  return null;
};

interface StatItemComponentProps {
  stat: StatItem;
  theme: Theme;
  variant: "h3" | "h4" | "h5" | "body1";
}

const StatItemComponent = ({
  stat,
  theme,
  variant,
}: StatItemComponentProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (stat.label) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const color = getColor(theme, stat.color);

  return (
    <>
      <Grid
        item
        display="flex"
        alignItems="center"
        onClick={handleClick}
        sx={{
          cursor: stat.label ? "pointer" : "default",
          "&:hover": stat.label
            ? {
                opacity: 0.8,
              }
            : {},
        }}
      >
        <Box
          sx={{
            color,
            display: "flex",
            alignItems: "center",
            mr: "2.5px",
          }}
        >
          {stat.icon}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant={variant}
            sx={{
              color,
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            {typeof stat.value === "number"
              ? stat.value.toLocaleString()
              : stat.value}
            {getDeltaIcon(stat.delta)}
          </Typography>
          {stat.title && (
            <Typography variant="caption" sx={{ color, whiteSpace: "nowrap" }}>
              {stat.title}
            </Typography>
          )}
        </Box>
      </Grid>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 280,
              borderRadius: 2,
            },
          },
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color, mb: 0.5 }}
        >
          {stat.label}
        </Typography>
      </Popover>
    </>
  );
};

const StatsBar = ({ stats, variant = "h3" }: StatsBarProps) => {
  const theme = useTheme();

  return (
    <Grid
      container
      sx={{
        alignItems: "center",
        justifyContent: { xs: "space-between", md: "space-evenly" },
        wrap: "nowrap",
        m: 1,
        alignSelf: "center",
      }}
    >
      {stats.map((stat) => (
        <Box key={stat.key} sx={{ display: "flex", alignItems: "center" }}>
          <StatItemComponent stat={stat} theme={theme} variant={variant} />
        </Box>
      ))}
    </Grid>
  );
};

export default StatsBar;
